<script setup lang="ts">
    import IconList from "@/components/calendars/overlayPanels/IconList/IconList.vue";
    import IconListItem from "@/components/calendars/overlayPanels/IconList/IconListItem.vue";
    import KTag from "@/components/KTag.vue";
    import { Forskrivningsstatus, ReceptforskrivningDisplay } from "@/tabs/Receptforskrivning/types";
    import moment from "moment";

    defineProps<{
        data: ReceptforskrivningDisplay;
    }>();

    const ejDelges = " (får ej delges patient)";
</script>

<template>
    <div class="recept-tab-forskrivning">
        <div class="left">
            <IconList>
                <IconListItem v-if="data.forskrivare?.fornamn" label="Förskrivare namn">
                    {{ `${data.forskrivare.fornamn} ${data.forskrivare.efternamn ?? ""}` }}
                </IconListItem>
                <IconListItem v-if="data.forskrivare?.roll" label="Förskrivare roll">{{
                    data.forskrivare?.roll
                }}</IconListItem>
                <IconListItem v-if="data.forskrivare?.telefonnummer?.length" label="Förskrivare telefonnummer">
                    <IconList>
                        <IconListItem
                            v-for="telefonnummer in data.forskrivare.telefonnummer"
                            :key="telefonnummer.value"
                        >
                            {{ `${telefonnummer.value}${telefonnummer.available_to_patient ? "" : ejDelges}` }}
                        </IconListItem>
                    </IconList>
                </IconListItem>
                <IconListItem
                    v-if="!data.forskrivare?.fornamn && data.forskrivare?.fullstandigt_namn"
                    label="Förskrivare fullständigt namn"
                >
                    {{ data.forskrivare.fullstandigt_namn }}
                </IconListItem>
                <IconListItem v-if="data.forskrivare?.landskod" label="Landskod">
                    {{ data.forskrivare.landskod }}
                </IconListItem>
            </IconList>
            <IconList>
                <IconListItem v-if="data.forskrivare?.vardenhet?.namn" label="Vårdenhet namn">{{
                    data.forskrivare?.vardenhet?.namn
                }}</IconListItem>
                <IconListItem v-if="data.forskrivare?.vardenhet?.adress" label="Vårdenhet adress">
                    {{ data.forskrivare?.vardenhet?.adress?.join(", ") }}
                </IconListItem>
                <IconListItem v-if="data.forskrivare?.vardenhet?.telefonnummer?.length" label="Vårdenhet telefonnummer">
                    <IconList>
                        <IconListItem
                            v-for="telefonnummer in data.forskrivare.vardenhet.telefonnummer"
                            :key="telefonnummer.value"
                        >
                            {{ `${telefonnummer.value}${telefonnummer.available_to_patient ? "" : ejDelges}` }}
                        </IconListItem>
                    </IconList>
                </IconListItem>
            </IconList>
            <IconList>
                <IconListItem :label="data.uttag_pappersrecept ? 'Uttagsstatus' : 'Förskrivningsstatus'">{{
                    data.forskrivningsstatus
                }}</IconListItem>
                <IconListItem v-if="data.forskrivningsstatusorsak" label="Förskrivningsstatusorsak">
                    {{ data.forskrivningsstatusorsak }}
                </IconListItem>
                <IconListItem v-if="data.handelseorsak" label="Händelseorsak">
                    {{ data.handelseorsak }}
                </IconListItem>
                <IconListItem v-if="data.beskrivning_handelseorsak" label="Beskrivning händelseorsak">
                    {{ data.beskrivning_handelseorsak }}
                </IconListItem>
                <IconListItem
                    :label="
                        data.uttag_pappersrecept
                            ? data.forskrivningsstatus === Forskrivningsstatus.Expedierat
                                ? 'Expedieringsdatum'
                                : 'Krediteringsdatum'
                            : 'Utfärdandedatum'
                    "
                >
                    {{ moment(data.utfardandedatum).format("YYYY-MM-DD HH:mm") }}
                </IconListItem>
                <IconListItem v-if="data.sista_giltighetsdag" label="Sista giltighetsdag">{{
                    data.sista_giltighetsdag
                }}</IconListItem>
            </IconList>
            <IconList>
                <IconListItem v-if="!data.uttag_pappersrecept" label="Typ av registreringsunderlag">
                    {{ data.typ_av_registreringsunderlag }}
                </IconListItem>
                <IconListItem v-if="!data.uttag_pappersrecept" label="Aktuellt format">
                    {{ data.aktuellt_format }}
                </IconListItem>
                <IconListItem v-for="varning in data.varningar ?? []" :key="varning.code" label="AFF-varning">
                    {{ varning.display }}
                </IconListItem>
            </IconList>
            <IconList>
                <IconListItem v-if="data.formansberattigad_patient" label="Förmånsberättigad patient">
                    {{ data.formansberattigad_patient ? "Ja" : "Nej" }}
                </IconListItem>
                <IconListItem
                    v-if="typeof data.formansvillkor_for_lakemedel_uppfyllda === 'boolean'"
                    label="Förmånsvillkor uppfyllda"
                >
                    {{ data.formansvillkor_for_lakemedel_uppfyllda ? "Ja" : "Nej" }}
                </IconListItem>
                <IconListItem
                    v-if="typeof data.forskrivning_enligt_smittskyddslagen === 'boolean'"
                    label="Förskrivning enligt smittskyddslagen"
                >
                    {{ data.forskrivning_enligt_smittskyddslagen ? "Ja" : "Nej" }}
                </IconListItem>
            </IconList>
            <IconList>
                <IconListItem v-if="data.forskrivarens_kommentar" label="Förskrivarens kommentar">
                    {{ data.forskrivarens_kommentar }}
                </IconListItem>
                <IconListItem v-if="data.apotekets_kommentar" label="Apotekets kommentar">
                    {{ data.apotekets_kommentar }}
                </IconListItem>
                <IconListItem v-if="data.notat_forskrivare" label="Notat förskrivare">
                    {{ data.notat_forskrivare }}
                </IconListItem>
                <IconListItem v-if="data.leveransmeddelande" label="Leveransmeddelande">
                    {{ data.leveransmeddelande }}
                </IconListItem>
            </IconList>
        </div>
        <div class="separator">
            <div class="line"></div>
        </div>
        <div class="right">
            <IconList>
                <IconListItem v-if="data.behandlingsorsak" label="Behandlingsorsak">
                    {{ data.behandlingsorsak }}
                </IconListItem>
                <IconListItem v-if="data.annan_orsak" label="Annan behandlingsorsak">
                    {{ data.annan_orsak }}
                </IconListItem>
                <IconListItem
                    v-if="data.senaste_datum_for_uppfoljning_av_behandling"
                    label="Senaste datum uppföljning behandling"
                >
                    {{ data.senaste_datum_for_uppfoljning_av_behandling }}
                </IconListItem>
                <IconListItem
                    v-if="data.senaste_datum_for_avslut_av_behandling"
                    label="Senaste datum avslut behandling"
                >
                    {{ data.senaste_datum_for_avslut_av_behandling }}
                </IconListItem>
                <IconListItem label="Doseringsanvisning">{{ data.doseringsanvisning }}</IconListItem>
            </IconList>
            <IconList>
                <IconListItem
                    v-if="data.atc !== undefined"
                    :label="data.uttag_pappersrecept ? 'Uttagstyp' : 'Förskrivningstyp'"
                >
                    {{
                        data.dosdispensering
                            ? "Dosdispensering"
                            : data.uttag_pappersrecept
                            ? "Helförpackningsuttag"
                            : "Helförpackning"
                    }}
                </IconListItem>
                <IconListItem v-if="data.sista_giltighetsdag" label="Sista giltighetsdag">{{
                    data.sista_giltighetsdag
                }}</IconListItem>
                <IconListItem v-if="data.substitution_tillaten" label="Substitution tillåten">
                    {{ data.substitution_tillaten ? "Ja" : "Nej" }}
                </IconListItem>
                <IconListItem v-if="data.forpackningsinnehall" label="Förpackningsinnehåll">
                    {{ data.forpackningsinnehall }}
                </IconListItem>
                <IconListItem v-if="data.antal_uttag" label="Förskrivet antal uttag">{{
                    data.antal_uttag
                }}</IconListItem>
                <IconListItem v-if="!data.uttag_pappersrecept" label="Antal uttag kvar">{{
                    data.uttag_kvar
                }}</IconListItem>
                <IconListItem
                    :label="
                        data.uttag_pappersrecept ? 'Antal expedierade förpackningar' : 'Antal förpackningar per uttag'
                    "
                    >{{ data.forpackningar_per_uttag }}</IconListItem
                >
                <IconListItem :label="data.uttag_pappersrecept ? 'Expedierad mängd' : 'Förskriven mängd per uttag'">
                    {{ data.forskriven_mangd_per_uttag ?? "-" }}
                </IconListItem>
                <IconListItem v-if="data.kvarvarande_mangd" label="Kvarvarande mängd">{{
                    data.kvarvarande_mangd
                }}</IconListItem>
            </IconList>
            <IconList>
                <IconListItem v-if="data.forsta_doseringsdag" label="Första doseringsdag">
                    {{ data.forsta_doseringsdag }}
                </IconListItem>
                <IconListItem v-if="data.sista_doseringsdag" label="Sista doseringsdag">
                    {{ data.sista_doseringsdag }}
                </IconListItem>
                <IconListItem v-if="data.tid_mellan_uttag" label="Tid mellan uttag">
                    {{ data.tid_mellan_uttag }}
                </IconListItem>
                <IconListItem v-if="typeof data.startforpackning === 'boolean'" label="Startförpackning">
                    {{ data.startforpackning ? "Ja" : "Nej" }}
                </IconListItem>
                <IconListItem v-if="data.forsta_uttag_fore" label="Första uttag före">
                    {{ data.forsta_uttag_fore }}
                </IconListItem>
                <IconListItem v-if="data.nasta_uttag_inom_forman" label="Nästa uttag inom förmån">
                    {{ data.nasta_uttag_inom_forman }}
                </IconListItem>
                <IconListItem v-if="data.obs_overstiger_maxdosering" label="Obs. Överstiger rekommenderad dos">
                    Ja
                </IconListItem>
                <IconListItem v-if="data.vid_behov" label="Vid behov"> Ja </IconListItem>
                <IconListItem v-if="data.akut_forandring" label="Akut förändring"> Ja </IconListItem>
                <IconListItem v-if="data.bytes_ej" label="Bytes ej">{{ data.bytes_ej }}</IconListItem>
            </IconList>
            <IconList>
                <IconListItem v-if="data.linkFass" label="Länk FASS">
                    <KTag variant="link" size="none">
                        <a :href="data.linkFass" target="_blank">Läs mer på FASS</a>
                    </KTag>
                </IconListItem>
            </IconList>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";

    .recept-tab-forskrivning {
        display: flex;
        flex: 1;
        gap: 1.5rem;
        width: 100%;
        max-width: $content-max-width;

        .left,
        .right {
            display: grid;
            grid-auto-flow: row;
            grid-auto-rows: max-content;
            grid-template-columns: repeat(2, auto);
            grid-row-gap: 1.5rem;
            flex: 1;
            max-width: max-content;

            ::v-deep .icon-list {
                display: grid;
                grid-column: 1 / -1;
                grid-template-columns: subgrid;
            }

            ::v-deep .icon-list-item {
                grid-auto-flow: row;

                .content {
                    width: 100%;
                }
            }
        }

        .separator {
            .line {
                border: 1px solid $divider-color;
                height: 100%;
            }
        }

        &.variant-simple {
            font-size: 0.9rem;
        }
    }
</style>
